import { useEffect, useRef } from 'react'
import './index.scss'

const StarField = () => {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    let width = window.innerWidth
    let height = window.innerHeight
    canvas.width = width
    canvas.height = height

    // --- Star layers (parallax depth) ---
    const LAYER_COUNT = 3
    const STARS_PER_LAYER = [120, 80, 40] // back → front (more small, fewer large)
    const SPEED = [0.08, 0.18, 0.35]
    const SIZE = [0.8, 1.4, 2.2]
    const OPACITY_RANGE = [
      [0.3, 0.7],
      [0.4, 0.8],
      [0.5, 1.0],
    ]

    const layers = []
    for (let l = 0; l < LAYER_COUNT; l++) {
      const stars = []
      for (let i = 0; i < STARS_PER_LAYER[l]; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          baseOpacity:
            OPACITY_RANGE[l][0] +
            Math.random() * (OPACITY_RANGE[l][1] - OPACITY_RANGE[l][0]),
          twinkleSpeed: 0.3 + Math.random() * 1.5,
          twinkleOffset: Math.random() * Math.PI * 2,
          size: SIZE[l] * (0.7 + Math.random() * 0.6),
        })
      }
      layers.push(stars)
    }

    // --- Shooting stars ---
    const shootingStars = []
    const spawnShootingStar = () => {
      shootingStars.push({
        x: Math.random() * width * 0.8,
        y: Math.random() * height * 0.4,
        len: 60 + Math.random() * 100,
        speed: 4 + Math.random() * 4,
        angle: (Math.PI / 6) + Math.random() * (Math.PI / 8),
        opacity: 1,
        decay: 0.012 + Math.random() * 0.008,
      })
    }

    // Spawn one every 4-8 seconds
    let shootTimer = null
    const scheduleShoot = () => {
      shootTimer = setTimeout(() => {
        spawnShootingStar()
        scheduleShoot()
      }, 4000 + Math.random() * 4000)
    }
    scheduleShoot()

    // --- Animation loop ---
    let raf
    let time = 0
    const draw = () => {
      ctx.clearRect(0, 0, width, height)
      time += 0.016 // ~60fps

      // Draw stars
      for (let l = 0; l < LAYER_COUNT; l++) {
        const stars = layers[l]
        const speed = SPEED[l]

        for (const star of stars) {
          star.y += speed
          if (star.y > height + 5) {
            star.y = -5
            star.x = Math.random() * width
          }

          const twinkle =
            0.5 + 0.5 * Math.sin(time * star.twinkleSpeed + star.twinkleOffset)
          const opacity = star.baseOpacity * (0.4 + 0.6 * twinkle)

          ctx.beginPath()
          ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`
          ctx.fill()

          // Add soft glow to front-layer stars
          if (l === LAYER_COUNT - 1 && star.size > 2) {
            ctx.beginPath()
            ctx.arc(star.x, star.y, star.size * 3, 0, Math.PI * 2)
            const grad = ctx.createRadialGradient(
              star.x, star.y, star.size * 0.5,
              star.x, star.y, star.size * 3
            )
            grad.addColorStop(0, `rgba(180, 200, 255, ${opacity * 0.25})`)
            grad.addColorStop(1, 'rgba(180, 200, 255, 0)')
            ctx.fillStyle = grad
            ctx.fill()
          }
        }
      }

      // Draw shooting stars
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const s = shootingStars[i]
        const dx = Math.cos(s.angle) * s.speed
        const dy = Math.sin(s.angle) * s.speed
        s.x += dx
        s.y += dy
        s.opacity -= s.decay

        if (s.opacity <= 0) {
          shootingStars.splice(i, 1)
          continue
        }

        const tailX = s.x - Math.cos(s.angle) * s.len
        const tailY = s.y - Math.sin(s.angle) * s.len

        const grad = ctx.createLinearGradient(tailX, tailY, s.x, s.y)
        grad.addColorStop(0, `rgba(255, 255, 255, 0)`)
        grad.addColorStop(1, `rgba(255, 255, 255, ${s.opacity})`)

        ctx.beginPath()
        ctx.moveTo(tailX, tailY)
        ctx.lineTo(s.x, s.y)
        ctx.strokeStyle = grad
        ctx.lineWidth = 1.5
        ctx.stroke()

        // Bright head
        ctx.beginPath()
        ctx.arc(s.x, s.y, 1.5, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255, 255, 255, ${s.opacity})`
        ctx.fill()
      }

      raf = requestAnimationFrame(draw)
    }

    raf = requestAnimationFrame(draw)

    // Resize handler
    const onResize = () => {
      width = window.innerWidth
      height = window.innerHeight
      canvas.width = width
      canvas.height = height
    }
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(shootTimer)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  return <canvas ref={canvasRef} className="starfield" />
}

export default StarField
